// BookingLeftPanel — shared sticky left rail for the book-in / modify /
// no-book-in / receipt-from-booking flows.
//
// Mirrors the legacy `includes/left_panel.php` (search-by-name, search-by-
// reg, action buttons + import-from-email) and adds the State B treatment
// the brief asks for: when a customer is selected, the panel switches from
// "search to find a customer" (State A) to "show this customer's summary
// and last 5 bookings" (State B).
//
// All API calls go through apiClient → JWT carries `cid` so every list
// the panel renders is already company-scoped server-side. The panel does
// NOT take companyId as a prop — it reads from the auth store implicitly
// via apiClient.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Mail,
  Clock,
  Calculator,
  Receipt as ReceiptIcon,
  ListFilter,
  Phone,
  MapPin,
  User,
  Car,
} from 'lucide-react';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useDebounce } from '../../../hooks/useDebounce';
import { Button } from '../Button/Button';
import { Badge, type BadgeVariant } from '../Badge/Badge';
import { QuoteModal } from '../QuoteModal/QuoteModal';
import type {
  BookingListResponse,
  BookingStatus,
  BookingSummary,
  CustomerBookingHistoryResponse,
  CustomerListResponse,
  CustomerSummary,
} from '../../../types/api';

const BOOKING_STATUS_VARIANTS: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancellation_requested: 'danger',
  cancelled: 'muted',
};

function statusLabel(s: BookingStatus): string {
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
import styles from './BookingLeftPanel.module.css';

const MIN_QUERY = 2;
const HISTORY_PREVIEW = 5;

export interface BookingLeftPanelProps {
  /** Current customer-attached state (State B) — null shows State A. */
  selectedCustomer: CustomerSummary | null;
  /** Fired when a customer is picked from the inline result list. */
  onCustomerSelect: (customer: CustomerSummary) => void;
  /** Fired when "Import from Email" is clicked. Falls back to a no-op
   *  + console warning when not provided so the button always renders. */
  onImportFromEmail?: () => void;
}

async function searchCustomers(search: string): Promise<CustomerSummary[]> {
  const res = await apiClient.get<CustomerListResponse>(
    endpoints.customers.list,
    { params: { search, limit: 8 } },
  );
  return res.data?.customers ?? [];
}

async function searchBookings(reg: string): Promise<BookingSummary[]> {
  const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, {
    params: { reg, limit: 8 },
  });
  return res.data?.bookings ?? [];
}

async function fetchHistory(uuid: string): Promise<CustomerBookingHistoryResponse> {
  const res = await apiClient.get<CustomerBookingHistoryResponse>(
    endpoints.customers.bookings(uuid),
    { params: { limit: HISTORY_PREVIEW } },
  );
  return res.data ?? { bookings: [], pagination: { page: 1, limit: HISTORY_PREVIEW, total: 0, total_pages: 1 } };
}

function formatDateShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatGbp(n: number): string {
  return `£${Number(n).toFixed(2)}`;
}

function bookingEditUrl(b: BookingSummary): string {
  return `/bookings/${encodeURIComponent(b.enc_id)}/edit`;
}

export function BookingLeftPanel({
  selectedCustomer,
  onCustomerSelect,
  onImportFromEmail,
}: BookingLeftPanelProps): JSX.Element {
  const navigate = useNavigate();

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [customerTerm, setCustomerTerm] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const debouncedCustomer = useDebounce(customerTerm, 300);
  const customerWrapRef = useRef<HTMLDivElement | null>(null);

  const [bookingTerm, setBookingTerm] = useState('');
  const [bookingOpen, setBookingOpen] = useState(false);
  const debouncedBooking = useDebounce(bookingTerm, 300);
  const bookingWrapRef = useRef<HTMLDivElement | null>(null);

  const customerSearchEnabled = debouncedCustomer.trim().length >= MIN_QUERY;
  const customersQuery = useQuery({
    queryKey: queryKeys.customers.list({ search: debouncedCustomer }),
    queryFn: () => searchCustomers(debouncedCustomer.trim()),
    enabled: customerSearchEnabled,
    staleTime: 30_000,
  });

  const bookingSearchEnabled = debouncedBooking.trim().length >= MIN_QUERY;
  const bookingsQuery = useQuery({
    queryKey: ['bookingLeftPanel', 'bookingSearch', debouncedBooking],
    queryFn: () => searchBookings(debouncedBooking.trim().toUpperCase()),
    enabled: bookingSearchEnabled,
    staleTime: 30_000,
  });

  const historyQuery = useQuery({
    queryKey: ['bookingLeftPanel', 'history', selectedCustomer?.uuid ?? null],
    queryFn: () => fetchHistory(selectedCustomer!.uuid),
    enabled: selectedCustomer !== null,
    staleTime: 30_000,
  });

  // Outside-click closes either result list.
  useEffect(() => {
    if (!customerOpen && !bookingOpen) return;
    function handle(e: MouseEvent) {
      const t = e.target as Node;
      if (customerWrapRef.current && !customerWrapRef.current.contains(t)) {
        setCustomerOpen(false);
      }
      if (bookingWrapRef.current && !bookingWrapRef.current.contains(t)) {
        setBookingOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [customerOpen, bookingOpen]);

  function handleCustomerPick(c: CustomerSummary) {
    onCustomerSelect(c);
    setCustomerTerm('');
    setCustomerOpen(false);
  }

  function handleBookingPick(b: BookingSummary) {
    setBookingTerm('');
    setBookingOpen(false);
    navigate(bookingEditUrl(b));
  }

  function handleImport() {
    if (onImportFromEmail) {
      onImportFromEmail();
    } else {
      // No-op fallback so the button is always present.
      console.warn(
        'BookingLeftPanel: onImportFromEmail not wired by this page',
      );
    }
  }

  const customerResults = customersQuery.data ?? [];
  const bookingResults = bookingsQuery.data ?? [];
  const history = historyQuery.data?.bookings ?? [];

  return (
    <aside className={styles.panel} aria-label="Booking left panel">
      {/* Customer Lookup */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <User size={12} aria-hidden /> Customer Lookup
        </h3>
        <div className={styles.searchWrap} ref={customerWrapRef}>
          <div className={styles.searchInputWrap}>
            <Search size={14} aria-hidden className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name or email..."
              autoComplete="off"
              aria-label="Customer search"
              value={customerTerm}
              onChange={(e) => {
                setCustomerTerm(e.target.value);
                setCustomerOpen(true);
              }}
              onFocus={() => setCustomerOpen(true)}
            />
          </div>
          {customerOpen && customerSearchEnabled ? (
            <div role="listbox" className={styles.results}>
              {customersQuery.isLoading ? (
                <div className={styles.empty}>Searching…</div>
              ) : customerResults.length === 0 ? (
                <div className={styles.empty}>No customers found</div>
              ) : (
                customerResults.map((c) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected="false"
                    key={c.uuid}
                    className={styles.resultRow}
                    onClick={() => handleCustomerPick(c)}
                  >
                    <span className={styles.resultName}>{c.full_name}</span>
                    <span className={styles.resultMeta}>
                      {c.phone}
                      {c.email ? ` · ${c.email}` : ''}
                    </span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Booking Lookup */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Car size={12} aria-hidden /> Booking Lookup
        </h3>
        <div className={styles.searchWrap} ref={bookingWrapRef}>
          <div className={styles.searchInputWrap}>
            <Search size={14} aria-hidden className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by registration..."
              autoComplete="off"
              aria-label="Booking search"
              value={bookingTerm}
              onChange={(e) => {
                setBookingTerm(e.target.value.toUpperCase());
                setBookingOpen(true);
              }}
              onFocus={() => setBookingOpen(true)}
            />
          </div>
          {bookingOpen && bookingSearchEnabled ? (
            <div role="listbox" className={styles.results}>
              {bookingsQuery.isLoading ? (
                <div className={styles.empty}>Searching…</div>
              ) : bookingResults.length === 0 ? (
                <div className={styles.empty}>No bookings found</div>
              ) : (
                bookingResults.map((b) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected="false"
                    key={b.enc_id}
                    className={styles.resultRow}
                    onClick={() => handleBookingPick(b)}
                  >
                    <span className={styles.resultName}>
                      {b.car_registration} — {b.car_make_model}
                    </span>
                    <span className={styles.resultMeta}>
                      {b.name} · {formatDateShort(b.date)}
                    </span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <ListFilter size={12} aria-hidden /> Quick Actions
        </h3>
        <div className={styles.actionGroup}>
          <Button
            variant="primary"
            icon={Mail}
            onClick={handleImport}
          >
            Import from Email
          </Button>
          <Button
            variant="secondary"
            icon={Clock}
            onClick={() => navigate('/collections/pending')}
          >
            Pending Bookings
          </Button>
          <Button
            variant="secondary"
            icon={ReceiptIcon}
            onClick={() => navigate('/collections/receipts/new')}
          >
            Create Receipt
          </Button>
          <Button
            variant="secondary"
            icon={Search}
            onClick={() => navigate('/collections/lookup')}
          >
            Advanced Search
          </Button>
          <Button
            variant="secondary"
            icon={Calculator}
            onClick={() => setQuoteOpen(true)}
          >
            Get Quote
          </Button>
        </div>
      </div>
      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />

      {/* State B — selected customer summary + history */}
      {selectedCustomer ? (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <User size={12} aria-hidden /> Selected Customer
          </h3>
          <div className={styles.summary}>
            <div className={styles.summaryName}>{selectedCustomer.full_name}</div>
            {selectedCustomer.phone ? (
              <div className={styles.summaryRow}>
                <Phone size={12} aria-hidden /> {selectedCustomer.phone}
              </div>
            ) : null}
            {selectedCustomer.email ? (
              <div className={styles.summaryRow}>
                <Mail size={12} aria-hidden /> {selectedCustomer.email}
              </div>
            ) : null}
            <div className={styles.summaryRow}>
              <MapPin size={12} aria-hidden />{' '}
              {selectedCustomer.address}
              {selectedCustomer.postcode ? `, ${selectedCustomer.postcode}` : ''}
            </div>
          </div>

          <h3 className={styles.sectionTitle} style={{ marginTop: '0.5rem' }}>
            Previous bookings
          </h3>
          {historyQuery.isLoading ? (
            <div className={styles.historyEmpty}>Loading…</div>
          ) : history.length === 0 ? (
            <div className={styles.historyEmpty}>No previous bookings</div>
          ) : (
            <div className={styles.history}>
              {history.slice(0, HISTORY_PREVIEW).map((h) => (
                <Link
                  key={h.uuid}
                  to={`/collections/bookings/${encodeURIComponent(h.uuid)}`}
                  className={styles.historyRow}
                >
                  <span className={styles.historyDate}>
                    {formatDateShort(h.date)}
                  </span>
                  <span className={styles.historyAmount}>
                    {formatGbp(h.price)}
                  </span>
                  <span className={styles.historyReg}>
                    {h.car_registration}
                    {' · '}
                    <Badge variant={BOOKING_STATUS_VARIANTS[h.status]}>
                      {statusLabel(h.status)}
                    </Badge>
                  </span>
                </Link>
              ))}
            </div>
          )}
          <button
            type="button"
            className={styles.viewAll}
            onClick={() => navigate(`/customers/${encodeURIComponent(selectedCustomer.uuid)}`)}
          >
            View all
          </button>
        </div>
      ) : null}
    </aside>
  );
}
